import { createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';
import API from '../../app/api';
import config from '../../app/config';
import { setSnackbar } from './globals';
import { getConfig } from '../../util/configUtil';
import {
    DEFAULT_ASSESSMENTS_VALUE,
    DEFAULT_EXT_DASHBOARD_VALUE,
    DEFAULT_LEVEL_VALUE_CLEANING_REQUEST,
    DEFAULT_LOCATION_VALUE_CLEANING_REQUEST,
    DEFAULT_OBS_CFG_VALUE,
    DEFAULT_PATIENT_VALUE,
    DEFAULT_REGISTRATION_VALUE,
    DEFAULT_TRIGGER_VALUE_CLEANING_REQUEST,
    DEFAULT_LOCAL_SETTINGS_VALUE,
    DEFAULT_WHITEBOARD_VALUE,
} from '../../pages/admin/aglyeConfig/configDefaultValues';

export const externalConfig = createSlice({
    name: 'externalConfig',
    initialState: {
        loading: undefined,
        configs: [],
        cleaningRequestCfg: {
            levels: DEFAULT_LEVEL_VALUE_CLEANING_REQUEST,
            location: DEFAULT_LOCATION_VALUE_CLEANING_REQUEST,
            triggers: DEFAULT_TRIGGER_VALUE_CLEANING_REQUEST,
            wards: [],
        },
        registrationCfg: DEFAULT_REGISTRATION_VALUE,
        localSettingsCfg: DEFAULT_LOCAL_SETTINGS_VALUE,
        patientCfg: DEFAULT_PATIENT_VALUE,
        observationCfg: DEFAULT_OBS_CFG_VALUE,
        assessmentCfg: DEFAULT_ASSESSMENTS_VALUE,
        extDashboardCfg: DEFAULT_EXT_DASHBOARD_VALUE,
        whiteboardCfg: DEFAULT_WHITEBOARD_VALUE,
    },
    reducers: {
        setConfig: (state, data) => {
            state.configs = data.payload;
        },
        setLoading: (state, data) => {
            state.loading = data.payload;
        },
        setCleaningRequestCfg: (state, data) => {
            state.cleaningRequestCfg = data.payload;
        },
        setRegistrationCfg: (state, data) => {
            state.registrationCfg = data.payload;
        },
        setPatientCfg: (state, data) => {
            state.patientCfg = data.payload;
        },
        setObservationCfg: (state, data) => {
            state.observationCfg = data.payload;
        },
        setAssessmentCfg: (state, data) => {
            state.assessmentCfg = data.payload;
        },
        setExtDashboardCfg: (state, data) => {
            state.extDashboardCfg = data.payload;
        },
        setLocalSettingsCfg: (state, data) => {
            state.localSettingsCfg = data.payload;
        },
        setWhiteboardCfg: (state, data) => {
            state.whiteboardCfg = data.payload;
        },
    },
});

// Setters
export const {
    setConfig,
    setLoading,
    setCleaningRequestCfg,
    setRegistrationCfg,
    setLocalSettingsCfg,
    setPatientCfg,
    setObservationCfg,
    setAssessmentCfg,
    setExtDashboardCfg,
    setWhiteboardCfg,
} = externalConfig.actions;

// Variable
export const externalConfigs = (state) => state.externalConfig.configs;
export const cleaningRequestConfigs = (state) => state.externalConfig.cleaningRequestCfg;
export const registrationConfig = (state) => state.externalConfig.registrationCfg;
export const patientConfig = (state) => state.externalConfig.patientCfg;
export const observationConfig = (state) => state.externalConfig.observationCfg;
export const assessmentsConfig = (state) => state.externalConfig.assessmentCfg;
export const extDashboardConfig = (state) => state.externalConfig.extDashboardCfg;
export const localSettingsConfig = (state) => state.externalConfig.localSettingsCfg;
export const whiteboardConfig = (state) => state.externalConfig.whiteboardCfg;

// Config
const localSystemConfig = getConfig('localSystemConfig.enabled', false);

// Methods
export const loadExternalConfigs = () => (dispatch, getState) => {
    if (getState().externalConfig.loading !== true) {
        dispatch(setLoading(true));

        API.getExternalConfig()
            .then((res) => {
                if (!_.isEmpty(res?.__data)) {
                    dispatch(setConfig(_.filter(res?.__data, (cfg) => cfg.active)));
                    const cfgCR = _.find(res.__data, { configType: 'bed.cleaning.request' });
                    const cfgReg = _.find(res.__data, { configType: 'registration' });
                    const cfgPt = _.find(res.__data, { configType: 'patients' });
                    const cfgObs = _.find(res.__data, { configType: 'observations' });
                    const cfgAss = _.find(res.__data, { configType: 'assessments' });
                    const cfgED = _.find(res.__data, { configType: 'ext.dashboards' });
                    const cfgWB = _.find(res.__data, { configType: 'whiteboard' });
                    if (cfgCR) dispatch(getCleaningRequestConfig(cfgCR));
                    if (cfgReg) dispatch(setRegistrationCfg(cfgReg.value));
                    if (cfgPt) dispatch(setPatientCfg(cfgPt.value));
                    if (cfgObs) dispatch(setObservationCfg(cfgObs.value));
                    if (cfgAss) dispatch(setAssessmentCfg(cfgAss.value));
                    if (cfgED) dispatch(setExtDashboardCfg(cfgED.value));
                    if (cfgWB) dispatch(setWhiteboardCfg(cfgWB.value));
                }
            })
            .catch((err) => {
                console.error(err);
                dispatch(
                    setSnackbar({
                        type: 'error',
                        message: 'Unable to load external configuration. Please reload the application.',
                    }),
                );
            })
            .finally(() => {
                dispatch(setLoading(undefined));
            });

        if (localSystemConfig) {
            API.getLocalSystemConfig()
                .then((res) => {
                    if (!_.isEmpty(res?.__data)) {
                        let d = res.__data;
                        dispatch(setLocalSettingsCfg(d));
                        // let d = res.__data.payload;
                        // console.log("res.__data:",d);

                        config.enableClinicalNotesFilters = getLocalSetting(d, 'enableClinicalNotesFilters');
                        config.admitPatient = {
                            //Paid
                            registration: getLocalSetting(d, 'admitPatient.registration'),
                            bedBooking: getLocalSetting(d, 'admitPatient.bedBooking'),
                            bedasListEnabled: getLocalSetting(d, 'admitPatient.bedasListEnabled'),
                            enableTemporaryPatientEdit: getLocalSetting(d, 'admitPatient.enableTemporaryPatientEdit'),
                            managePreviousAttendances: getLocalSetting(d, 'admitPatient.managePreviousAttendances'),
                            contactNumbersReadOnly: getLocalSetting(d, 'admitPatient.contactNumbersReadOnly'),
                            enablePriorityList: getLocalSetting(d, 'admitPatient.enablePriorityList'),
                            enablePatientMerge: getLocalSetting(d, 'admitPatient.enablePatientMerge'),
                            reAttendanceEnabled: getLocalSetting(d, 'admitPatient.reAttendanceEnabled'),
                            patientCreationEnabled: getLocalSetting(d, 'admitPatient.patientCreationEnabled'),
                            searchDbForNhsOnFirstRegister: getLocalSetting(d, 'admitPatient.searchDbForNhsOnFirstRegister'),
                            temporaryAddressEnabled: getLocalSetting(d, 'admitPatient.temporaryAddressEnabled'),
                            postalCodeValidation: getLocalSetting(d, 'admitPatient.postalCodeValidation'),
                            enableNFAButton: getLocalSetting(d, 'admitPatient.enableNFAButton'),
                            enableNFAButtonTemporary: getLocalSetting(d, 'admitPatient.enableNFAButtonTemporary'),
                            additionalRequiredPatientFields: getLocalSetting(d, 'admitPatient.additionalRequiredPatientFields'),
                        };
                        config.timestampBlockedDischarge = getLocalSetting(d, 'timestampBlockedDischarge'); // Fortrus
                        config.prescriptionDischargeEnabled = getLocalSetting(d, 'prescriptionDischargeEnabled'); // Paid
                        config.casCardLiteDischargeEnabled = getLocalSetting(d, 'casCardLiteDischargeEnabled'); // Fortrus
                        config.triageTreatmentForm = getLocalSetting(d, 'triageTreatmentForm'); // Fortrus
                        config.specialSmallDeviceName = getLocalSetting(d, 'specialSmallDeviceName'); // Fortrus
                        config.smallDevice = {
                            // Fortrus
                            smallDeviceSwitchEnable: getLocalSetting(d, 'smallDevice.smallDeviceSwitchEnable'),
                            smallDeviceMinWidth: getLocalSetting(d, 'smallDevice.smallDeviceMinWidth'),
                            debugUserAgent: getLocalSetting(d, 'smallDevice.debugUserAgent'),
                        };
                        config.ttaDrugPrescriptionEnabled = getLocalSetting(d, 'ttaDrugPrescriptionEnabled\n'); // Fortrus
                        config.sticker = getLocalSetting(d, 'sticker'); // Fortrus
                        config.disableShowPassword = getLocalSetting(d, 'disableShowPassword'); // Fortrus
                        config.accReferralForm = getLocalSetting(d, 'accReferralForm'); // Fortrus
                        config.dvtReferralForm = getLocalSetting(d, 'dvtReferralForm'); // Fortrus
                        config.aecsReferralForm = getLocalSetting(d, 'aecsReferralForm'); // Fortrus
                        config.ambulatoryCareForm = getLocalSetting(d, 'ambulatoryCareForm'); // Fortrus
                        config.roaENTForm = getLocalSetting(d, 'roaENTForm'); // Fortrus
                        config.roaEmergencyGynaeForm = getLocalSetting(d, 'roaEmergencyGynaeForm'); // Fortrus
                        config.roaEarlyPregnancyForm = getLocalSetting(d, 'roaEarlyPregnancyForm'); // Fortrus
                        config.emergencyDepartmentReferralForm = getLocalSetting(d, 'emergencyDepartmentReferralForm'); // Fortrus
                        config.roaRapidAccessChestPainForm = getLocalSetting(d, 'roaRapidAccessChestPainForm'); // Fortrus
                        config.roaMaxilloForm = getLocalSetting(d, 'roaMaxilloForm'); // Fortrus
                        config.pneumothoraxForm = getLocalSetting(d, 'pneumothoraxForm'); // Fortrus
                        config.fastTrackProtocolForm = getLocalSetting(d, 'fastTrackProtocolForm'); // Fortrus
                        config.respiratoryReferralForm = getLocalSetting(d, 'respiratoryReferralForm'); // Fortrus
                        config.casCardOnHL7 = getLocalSetting(d, 'casCardOnHL7'); //Fortrus
                        config.enableIceDesktop = getLocalSetting(d, 'enableIceDesktop'); // Fortrus
                        config.nurseLetterGenerateAfterDischarge = getLocalSetting(d, 'nurseLetterGenerateAfterDischarge'); // Fortrus
                        config.dischargeEmailSendAddress = {
                            // Fortrus
                            poole: getLocalSetting(d, 'dischargeEmailSendAddress.poole'),
                            other: getLocalSetting(d, 'dischargeEmailSendAddress.other'),
                            aboveFive: getLocalSetting(d, 'dischargeEmailSendAddress.aboveFive'),
                        };
                        config.customPrescriptionPrintForm = getLocalSetting(d, 'customPrescriptionPrintForm'); // Fortrus
                        config.healthVisitorReportsVisibleInDocuments = getLocalSetting(d, 'healthVisitorReportsVisibleInDocuments'); //Fortrus
                        config.postEditDischargeForm = getLocalSetting(d, 'postEditDischargeForm'); // Paid
                        config.nonContactObsAvailable = getLocalSetting(d, 'nonContactObsAvailable'); // Paid
                        config.bedasSessionManagementEnabled = getLocalSetting(d, 'bedasSessionManagementEnabled'); // Paid
                        config.clerkingCprVisible = getLocalSetting(d, 'clerkingCprVisible'); // Paid
                        config.trudSurgieriesApiSync = getLocalSetting(d, 'trudSurgieriesApiSync'); // Paid
                        config.referralReviewForm = getLocalSetting(d, 'referralReviewForm'); // Paid/Fortrus
                        config.disablePreRegList = getLocalSetting(d, 'disablePreRegList'); //Fortrus ask Michał

                        config.allergiesNKDA = getLocalSetting(d, 'allergiesNKDA');
                        config.alternativeWorkstreamTriage = getLocalSetting(d, 'alternativeWorkstreamTriage');
                        config.CFSMandatory = getLocalSetting(d, 'CFSMandatory');
                        config.chartTemperatureColor = getLocalSetting(d, 'chartTemperatureColor');
                        config.clerkingExaminationGuide = getLocalSetting(d, 'clerkingExaminationGuide');
                        config.clerkingGuideWithCardio = getLocalSetting(d, 'clerkingGuideWithCardio');
                        config.clerkingReview = getLocalSetting(d, 'clerkingReview');
                        config.clinicalConclusionNoteEnabled = getLocalSetting(d, 'clinicalConclusionNoteEnabled');
                        config.copyObsButtonsAvailable = getLocalSetting(d, 'copyObsButtonsAvailable');
                        config.diagnosisColorsConfirm.chip = getLocalSetting(d, 'diagnosisColorsConfirm.chip');
                        config.diagnosisColorsConfirm.main = getLocalSetting(d, 'diagnosisColorsConfirm.main');
                        config.diagnosisColorsSuspected.chip = getLocalSetting(d, 'diagnosisColorsSuspected.chip');
                        config.diagnosisColorsSuspected.main = getLocalSetting(d, 'diagnosisColorsSuspected.main');
                        config.diseaseInDischarge = getLocalSetting(d, 'diseaseInDischarge');
                        config.dropdownsWithCliniciansAndNurses = getLocalSetting(d, 'dropdownsWithCliniciansAndNurses');
                        config.enableClinicalNotesFilters = getLocalSetting(d, 'enableClinicalNotesFilters');
                        config.enabledCurrentClinician = getLocalSetting(d, 'enabledCurrentClinician');
                        config.formsTabHidden = getLocalSetting(d, 'formsTabHidden');
                        config.fractureFlagEnabled = getLocalSetting(d, 'fractureFlagEnabled');
                        config.mentalHealthFlagEnabled = getLocalSetting(d, 'mentalHealthFlagEnabled');
                        config.mentalHealthFormDialog = getLocalSetting(d, 'mentalHealthFormDialog');
                        config.modules = {
                            audits: {
                                pageSize: getLocalSetting(d, 'modules.audits.pageSize'),
                            },
                            safeguardingEmail: {
                                mail: {
                                    emails: getLocalSetting(d, 'modules.safeguardingEmail.mail.emails'),
                                    external: getLocalSetting(d, 'modules.safeguardingEmail.mail.external'),
                                    name: getLocalSetting(d, 'modules.safeguardingEmail.mail.name'),
                                },
                                text: getLocalSetting(d, 'modules.safeguardingEmail.text'),
                            },
                            dischargeFollowUpCheck: {
                                enabled: getLocalSetting(d, 'modules.dischargeFollowUpCheck.enabled'),
                                fractureClinic: getLocalSetting(d, 'modules.dischargeFollowUpCheck.fractureClinic'),
                                fractureClinicFormId: getLocalSetting(d, 'modules.dischargeFollowUpCheck.fractureClinicFormId'),
                                physiotherapy: getLocalSetting(d, 'modules.dischargeFollowUpCheck.physiotherapy'),
                                mandatory: getLocalSetting(d, 'modules.dischargeFollowUpCheck.mandatory'),
                            },
                            wardCreator: {
                                // paid (after develoop default enable)
                                enabled: getLocalSetting(d, 'modules.wardCreator.enabled'),
                                defaultHospitalId: getLocalSetting(d, 'modules.wardCreator.defaultHospitalId'),
                                admitModeEnable: getLocalSetting(d, 'modules.wardCreator.admitModeEnable'),
                                enableMultiWardMap: getLocalSetting(d, 'modules.wardCreator.enableMultiWardMap'),
                                wardManagement: getLocalSetting(d, 'modules.wardCreator.wardManagement'),
                                dashboard: {
                                    enabled: getLocalSetting(d, 'modules.wardCreator.dashboard.enabled'),
                                },
                            },
                            bedas: {
                                // paid
                                enabled: getLocalSetting(d, 'modules.bedas.enabled'),
                                minutesRemain: getLocalSetting(d, 'modules.bedas.minutesRemain'),
                                bedasOldCalendar: getLocalSetting(d, 'modules.bedas.bedasOldCalendar'),
                                allowMultiplePatientsOnOneHour: getLocalSetting(d, 'modules.bedas.allowMultiplePatientsOnOneHour'),
                            },
                            postCodeFinder: {
                                // paid
                                enabled: getLocalSetting(d, 'modules.postCodeFinder.enabled'),
                                size: getLocalSetting(d, 'modules.postCodeFinder.size'),
                            },
                            // config.clinicalNotes = { // free
                            //     showAllAttendance: getLocalSetting(d,"TODO"),
                            //     enabled: getLocalSetting(d,"TODO"),
                            //     bedRequest: getLocalSetting(d,"TODO"),
                            // };
                            bedManagement: {
                                // paid
                                enabled: getLocalSetting(d, 'modules.bedManagement.enabled'),
                                bedRequest: getLocalSetting(d, 'modules.bedManagement.bedRequest'),
                            },
                            cleaningRequest: {
                                // paid
                                enabled: getLocalSetting(d, 'modules.cleaningRequest.enabled'),
                            },
                            // config.centralPage = { // free(def. true) (after develoop default enable)
                            //     enabled: getLocalSetting(d,"TODO"),
                            //     supportTabEnabled: getLocalSetting(d,"TODO"),
                            //     supportURL: getLocalSetting(d,"TODO"),
                            // };
                            bars: {
                                // paid
                                enabled: getLocalSetting(d, 'modules.bars.enabled'),
                            },
                            scheduler: {
                                // paid
                                enabled: getLocalSetting(d, 'modules.scheduler.enabled'),
                                buttonInDischarge: getLocalSetting(d, 'modules.scheduler.buttonInDischarge'),
                            },
                            // config.splitScreen : { // free (TBR)
                            //     enabled: getLocalSetting(d,"TODO"),
                            // };
                            formSearch: {
                                // Fortrus
                                enabled: getLocalSetting(d, 'modules.formSearch.enabled'),
                                formList: getLocalSetting(d, 'modules.formSearch.formList'),
                                formsDisplayName: getLocalSetting(d, 'modules.formSearch.formsDisplayName'),
                            },
                            forms: {
                                bedBooking: {
                                    // paid
                                    enableBedRequestForm: getLocalSetting(d, 'modules.forms.bedBooking.enableBedRequestForm'),
                                    enableAnotherDestination: getLocalSetting(d, 'modules.forms.bedBooking.enableAnotherDestination'),
                                },
                                //ecds: { // fortrus
                                //    activationDateOfVersion4: getLocalSetting(d,"TODO"),
                                //    diagnosisMaxEnabled: getLocalSetting(d,"TODO"),
                                //},
                                // discharge: { // free
                                //     enableDestinationNokQuestion: getLocalSetting(d,"TODO"),
                                //     nokQuestionDestinationsCodes: getLocalSetting(d,"TODO"),
                                // },
                            },
                            admin: {
                                // fortrus
                                system: getLocalSetting(d, 'modules.admin.system'),
                            },

                            // config.alerts = { // Free
                            //     defaultDuration: getLocalSetting(d,"TODO"),
                            // };
                            patient: {
                                dodDateUpdateEnabled: getLocalSetting(d, 'modules.patient.dodDateUpdateEnabled'), //Fortrus
                                // showAttendanceDetails: getLocalSetting(d,"TODO"), //TBR
                                disabledFlags: getLocalSetting(d, 'modules.patient.disabledFlags'), //Fortrus
                            },
                            patients: {
                                pageSize: getLocalSetting(d, 'modules.patients.pageSize'), // Fortrus
                            },
                            reports: {
                                // Fortrus
                                extendedDashboard: getLocalSetting(d, 'modules.reports.extendedDashboard'),
                                extendedDashboardDataRefreshInterval: getLocalSetting(
                                    d,
                                    'modules.reports.extendedDashboardDataRefreshInterval',
                                ), // in millisecond
                                extendedDashboardLineWidth: getLocalSetting(d, 'modules.reports.extendedDashboardLineWidth'),
                                patientTimeline: getLocalSetting(d, 'modules.reports.patientTimeline'),
                            },
                        };

                        config.neuroNextObsTimeAvailable = getLocalSetting(d, 'neuroNextObsTimeAvailable');
                        config.neuroObsLimbMovExpanded = getLocalSetting(d, 'neuroObsLimbMovExpanded');
                        config.patientSafetyChecklist = {
                            allFieldsMandatory: getLocalSetting(d, 'patientSafetyChecklist.allFieldsMandatory'),
                            clientVersion: getLocalSetting(d, 'patientSafetyChecklist.clientVersion'),
                            disableReadyToAdmission: getLocalSetting(d, 'patientSafetyChecklist.disableReadyToAdmission'),
                            disableRosierAssessment: getLocalSetting(d, 'patientSafetyChecklist.disableRosierAssessment'),
                            enabled: getLocalSetting(d, 'patientSafetyChecklist.enabled'),
                            green: getLocalSetting(d, 'patientSafetyChecklist.green'),
                            IVCannulaMandatory: getLocalSetting(d, 'patientSafetyChecklist.IVCannulaMandatory'),
                            orange: getLocalSetting(d, 'patientSafetyChecklist.orange'),
                            red: getLocalSetting(d, 'patientSafetyChecklist.red'),
                            timeoutMinutesX: getLocalSetting(d, 'patientSafetyChecklist.timeoutMinutesX'),
                            timeoutMinutesY: getLocalSetting(d, 'patientSafetyChecklist.timeoutMinutesY'),
                        };
                        config.priorAnalgesiaInTriage = getLocalSetting(d, 'priorAnalgesiaInTriage');
                        config.safeguardingFormModalInDis = getLocalSetting(d, 'safeguardingFormModalInDis');
                        config.safeguardingLevelMandatory = getLocalSetting(d, 'safeguardingLevelMandatory');
                        config.safetyListButtonInObs = getLocalSetting(d, 'safetyListButtonInObs');
                        config.showDifferentialDiagnosisSeeAndTreat = getLocalSetting(d, 'showDifferentialDiagnosisSeeAndTreat');
                        config.showRapidAssessmentDictionaryDropdown = getLocalSetting(d, 'showRapidAssessmentDictionaryDropdown');
                        config.timeout = {
                            mobileDefaultTimeout: getLocalSetting(d, 'timeout.mobileDefaultTimeout'),
                            mobileMinutesBeforeTimeoutToShowInfo: getLocalSetting(d, 'timeout.mobileMinutesBeforeTimeoutToShowInfo'),
                            OverwriteTimeoutDuration: getLocalSetting(d, 'timeout.OverwriteTimeoutDuration'),
                            regularDefaultTimeout: getLocalSetting(d, 'timeout.regularDefaultTimeout'),
                            regularMinutesBeforeTimeoutToShowInfo: getLocalSetting(d, 'timeout.regularMinutesBeforeTimeoutToShowInfo'),
                            UsersForTimeoutOverwrite: getLocalSetting(d, 'timeout.UsersForTimeoutOverwrite'),
                        };
                        config.treatmentInvColors = {
                            chip: getLocalSetting(d, 'treatmentInvColors.chip'),
                            main: getLocalSetting(d, 'treatmentInvColors.main'),
                        };
                        config.triageMandatoryToDischarge = getLocalSetting(d, 'triageMandatoryToDischarge');
                        config.triageTreatmentForm = getLocalSetting(d, 'triageTreatmentForm');
                        config.trudSurgieriesApiSync = getLocalSetting(d, 'trudSurgieriesApiSync');
                        config.ttaDrugPrescriptionEnabled = getLocalSetting(d, 'ttaDrugPrescriptionEnabled');
                        config.ucrTabHidden = getLocalSetting(d, 'ucrTabHidden');
                        config.unescortedForm = getLocalSetting(d, 'unescortedForm');
                    }
                })
                .catch((err) => {
                    console.error(err);
                    dispatch(
                        setSnackbar({
                            type: 'error',
                            message: 'Unable to load local system configuration. Please reload the application.',
                        }),
                    );
                })
                .finally(() => {
                    dispatch(setLoading(undefined));
                });
        }
    }
};

export const getLocalSetting = (data, name) => {
    return _.find(data, (item) => item.configType === name)?.value || null;
};

export const getExternalConfigs = (type) => (dispatch, getState) => {
    if (getState().externalConfig.loading !== true) {
        return _.find(getState().externalConfig.configs, (cfg) => cfg.configType === type) || null;
    }
};

export const saveExternalConfigs = (data) => (dispatch, getState) => {
    if (getState().externalConfig.loading !== true) {
        API.saveExternalConfig(data)
            .then((_res) => {
                dispatch(loadExternalConfigs());
                dispatch(
                    setSnackbar({
                        type: 'success',
                        message: 'Configuration saved successfully. Please reload the application.',
                    }),
                );
            })
            .catch((err) => {
                console.error(err);
                dispatch(
                    setSnackbar({
                        type: 'error',
                        message: 'Unable to save external configuration.',
                    }),
                );
            });
    }
};

export const saveLocalSettingsConfig = (data) => (dispatch, getState) => {
    if (getState().externalConfig.loading !== true) {
        API.saveLocalSystemConfig(data)
            .then((_res) => {
                dispatch(loadExternalConfigs());
                dispatch(
                    setSnackbar({
                        type: 'success',
                        message: 'Configuration saved successfully. Please reload the application.',
                    }),
                );
            })
            .catch((err) => {
                console.error(err);
                dispatch(
                    setSnackbar({
                        type: 'error',
                        message: 'Unable to save local system configuration.',
                    }),
                );
            });
    }
};

const getCleaningRequestConfig = (cfg) => (dispatch, getState) => {
    if (cfg) {
        const mergedConfigs = Object.assign({}, getState().externalConfig.cleaningRequestCfg, cfg.value);
        dispatch(setCleaningRequestCfg(mergedConfigs));
    }
};

export default externalConfig.reducer;
